import React from 'react';
import type { UnitType } from 'dayjs';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { sha256 } from 'js-sha256';
import UAParser from 'ua-parser-js';
import {
  SERVICE_ENTITLEMENTS_MAPPING,
  SERVICES,
} from 'utils/service-entitlements-config';
import type { IBureau } from 'services/credit-score/types';
import { rawEntitlementsIncludeNewsArticles } from 'services/session/utils';
import {
  FLEX_DIT_VERIFY_DENY_CODE,
  FLEX_DIT_VERIFY_PROPERTY,
} from 'services/enrollment/config';
import {
  BROWSER,
  OS,
  STORAGE_KEYS,
  REG_EX,
  CREDIT_BUREAU_CONFIGS,
  CREDIT_FEATURES,
  WGS_SETUP_WINDOW_DAYS,
} from './constants';
import {
  LooseObjectType,
  PhoneNumberDelimiterType,
  ServicesType,
} from './globalTypes';

dayjs.extend(duration);
dayjs.extend(relativeTime);

/**
 * Function to parse the json data
 */
export function parseJSON(
  data: string | null | undefined,
  defaultValue: unknown,
) {
  return JSON.parse(data || JSON.stringify(defaultValue));
}

/**
 * Function to store the value in local storage under specific key
 */

export function setInStorage(key: string, value: unknown) {
  sessionStorage.setItem(key, JSON.stringify(value));
}

/**
 * Function to get the specific key value from the local storage
 */

export function getFromStorage(key: string, defaultValue: unknown = '') {
  return parseJSON(sessionStorage.getItem(key), defaultValue);
}

/**
 * Function to romove the specific key from the local storage
 */
export function removeFromStorage(key: string) {
  sessionStorage.removeItem(key);
}

/**
 * Function to read access token from persist store
 */
export function getAccessToken(): string | null {
  const sessionData = getFromStorage(STORAGE_KEYS.persistRoot, {})?.session;
  if (sessionData) {
    return parseJSON(sessionData, {}).accessToken;
  }
  return null;
}

/**
 * Function to mask the string upto the last specified charecters with the specified character
 */

export function maskify(str: string, maskChar = '•', length = 4) {
  const pattern = `.(?=.{${length}})`;
  const regExpression = new RegExp(pattern, 'g');
  return str.replace(regExpression, maskChar);
}

/**
 * Function will take any string formatted text and convert it to phone number formatted string with the delimiter character
 */

export function formatAsPhoneNumber(
  str: string,
  delimiter: PhoneNumberDelimiterType = '-',
) {
  function phoneNumber(str2: string) {
    return (
      str2.slice(0, 3) +
      delimiter +
      str2.slice(3, 6) +
      delimiter +
      str2.slice(6)
    );
  }

  if (!str) {
    return '';
  }

  if (delimiter === '(xxx) xxx-xxxx') {
    return `(${str.slice(0, 3)}) ${str.slice(3, 6)}-${str.slice(6)}`;
  }

  if (str.length === 11) {
    return str.slice(0, 1) + delimiter + phoneNumber(str.slice(1));
  }

  return phoneNumber(str);
}

/**
 * Function to convert as the phone number formatted string by removing all non digit characters
 *
 * return (123) 456-7890 or 123-456-7890
 */
export function formatPhoneNumber(
  phoneNumberString: string,
  delimiter: PhoneNumberDelimiterType = '-',
) {
  const cleaned = phoneNumberString.replace(/\D/g, '');
  return cleaned ? formatAsPhoneNumber(cleaned, delimiter) : null;
}

/**
 * Normalise a stored phone number to a bare ten-digit US string, dropping formatting characters
 * and a leading "1" country code.
 *
 *   "+1 (987) 654-3210" → "9876543210"
 *   "19876543210"       → "9876543210"
 *   "9876543210"        → "9876543210"
 *
 * Every phone form field is a ten-digit `(000) 000-0000` mask validated at exactly ten
 * characters, so an eleven-digit stored value has to be normalised before it seeds one: fed in
 * raw it masks as `(198) 765-4321` and fails validation on a value the member never typed. Use
 * this on anything read out of the store and into a phone field.
 */
export function toTenDigitPhone(raw: string | null | undefined): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
}

/**
 * Function to check the input date is valid or not
 */
export function isValidDate(date: string | undefined | null) {
  if (!date) {
    return false;
  }

  return dayjs(date).isValid();
}

/**
 * Function to get date in specified format
 */
export function formatDate(
  date: string | undefined | null,
  format = 'MM/DD/YYYY',
): string {
  if (!date) {
    return 'NA';
  }

  return dayjs(date).isValid() ? dayjs(date).format(format) : 'NA';
}

/* Function to format the date from timestamp */
export function formatDateTimeStamp(
  timestamp: number,
  format = 'MMM DD, YYYY',
) {
  if (!timestamp) {
    return '-';
  }

  const date =
    timestamp.toString().length === 10
      ? dayjs.unix(timestamp)
      : dayjs(timestamp);
  return date.isValid() ? date.format(format) : '-';
}

/**
 * Function to get the humanize format date
 */
export function humanizeDate(data: {
  date: string | null;
  refDate?: string;
  unit?: Exclude<UnitType, 'date' | 'dates'>;
}) {
  const { date, refDate, unit = 'days' } = data;
  const date1 = dayjs(date);
  const date2 = refDate ? dayjs(refDate) : dayjs();

  if (!date1.isValid() || !date2.isValid()) {
    return null;
  }

  return dayjs.duration(date1.diff(date2, unit), unit).humanize(true);
}

/**
 * Whether an account has passed its setup window (days since creation exceeds
 * the threshold). Ports Angular's `isEligibleToShownCompleteSetupInfo`
 * (non-modal path: `diffDays > windowDays`). Used to gate the White Glove
 * Service upsell banner.
 */
export function isPastSetupWindow(
  createdAt: string | null | undefined,
  windowDays = WGS_SETUP_WINDOW_DAYS,
): boolean {
  if (!createdAt || !dayjs(createdAt).isValid()) {
    return false;
  }
  return dayjs().diff(dayjs(createdAt), 'days') > windowDays;
}

/**
 * Function to return the sha256 hash in hex string
 */
export function getHashInHex(message: string) {
  if (!message) {
    return message;
  }

  return sha256.hex(message);
}

/**
 * Function to get the last specified charecters of a string
 */
export function getLastChars(str: string, chars = 4) {
  if (!str) {
    return str;
  }

  return str.slice(-chars);
}

export function getOS(): string {
  const parser = new UAParser();

  const osName = parser.getResult().os.name;
  if (osName) {
    if (/Mac OS/.test(osName)) {
      return OS.mac;
    }
    if (/Windows/.test(osName)) {
      return OS.windows;
    }
    if (/iOS/.test(osName)) {
      return OS.ios;
    }
    if (/Android/.test(osName)) {
      return OS.android;
    }
  }
  return OS.other;
}

const ENCODED_PREFIX = 'b64:';

export const decodeRichText = (html: string | null | undefined): string => {
  if (!html) {
    return '';
  }

  if (!html.startsWith(ENCODED_PREFIX)) {
    return html;
  }

  const encoded = html.slice(ENCODED_PREFIX.length);
  const binary = atob(encoded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));

  return new TextDecoder().decode(bytes);
};

export function getBrowser(): string {
  const parser = new UAParser();
  const browserName = parser.getResult().browser.name;
  switch (browserName) {
    case BROWSER.chrome:
      return BROWSER.chrome;
    case BROWSER.msEdge:
      return BROWSER.msEdge;
    case BROWSER.firefox:
      return BROWSER.firefox;
    case BROWSER.safari:
      return BROWSER.safari;
    default:
      return BROWSER.other;
  }
}

export function getDevice() {
  const parser = new UAParser();
  return parser.getResult().device.type;
}

// to get Password Strength Indicator
export function getPasswordStrength(type: number) {
  const strengthIndicators = ['Bad', 'Weak', 'Good', 'Strong', 'Excellent'];
  return strengthIndicators[type] ?? 'Unknown';
}

/* Get file extension from file name */
export function getFileExtension(filename: string) {
  return filename.split('.').pop() ?? filename;
}

/* Truncate Text using ellipsis */
export function truncateText(text: string, maxLength = 20) {
  if (text.length > maxLength) {
    return `${text.slice(0, maxLength)}...`;
  }
  return text;
}

/**
 * Capitalizes the first alphabetic letter of each word in a string and lowercases
 * the rest. Handles multiple consecutive whitespace characters between words,
 * normalizing them to a single space in the output. Words that begin with special
 * characters (e.g. `&`) have their first alphabetic character capitalized.
 *
 * @example
 * capitalize('hello world')        // 'Hello World'
 * capitalize('hello   world')      // 'Hello World'
 * capitalize('HELLO  & WORLD')    // 'Hello & World'
 */
export function capitalize(str: string) {
  if (!str) {
    return str;
  }

  return str
    .trim()
    .split(/\s+/)
    .map((word) => word.toLowerCase().replace(/[a-z]/, (c) => c.toUpperCase()))
    .join(' ');
}

export function toSentenceCase(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/* Function to get the base64 url of a file */
export function getBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

/* Function to download a base64 payload (bare or data-URL) as a file */
export function downloadBase64Url(base64Url: string, fileName: string) {
  const payload = base64Url.includes('base64,')
    ? base64Url.slice(base64Url.indexOf('base64,') + 'base64,'.length)
    : base64Url;
  const bytes = Uint8Array.from(atob(payload), (c) => c.charCodeAt(0));
  const objectUrl = URL.createObjectURL(
    new Blob([bytes], { type: 'application/octet-stream' }),
  );

  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName || 'downloaded-file';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

/* Function converting Unicode to Character */
export function ConvertUnicodeToCharacter(text: string) {
  const decoded = text.replace(/&#x([0-9a-f]+);/gi, (_, code) =>
    String.fromCharCode(parseInt(code, 16)),
  );
  return decoded;
}

export function decodeHtmlEntities(text: string): string {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.body.textContent ?? text;
}

export function buildAddress(address: LooseObjectType) {
  return `${address?.street_line} ${address?.secondary} ${address?.city}, ${address?.state} ${address?.zipcode}`;
}

export function addSearchParams(url: string, params: LooseObjectType = {}) {
  const urlObj = new URL(url);

  return new URL(
    `${urlObj.origin}${urlObj.pathname}?${new URLSearchParams([
      ...Array.from(urlObj.searchParams.entries()),
      ...Object.entries(params),
    ])}`,
  ).toString();
}

export function addCarrierIdSearchParam(url: string, carrierId: string) {
  return addSearchParams(url, {
    carrier_id: carrierId,
  });
}

export function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 Bytes';

  const units = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  const size = (bytes / 1024 ** i).toFixed(1);

  return `${
    parseFloat(size) === Math.floor(parseFloat(size))
      ? Math.floor(parseFloat(size))
      : size
  } ${units[i]}`;
}

export function numericValuesAllowed(e: React.KeyboardEvent<HTMLDivElement>) {
  if ('key' in e) {
    const { key, ctrlKey } = e;
    const allowedKeys = new Set([
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Delete',
      'Enter',
    ]);

    if (!/^\d$/.test(key) && !ctrlKey && !allowedKeys.has(key)) {
      e.preventDefault();
    }
  }
}

export function pasteNumericValuesAllowed(
  e: React.ClipboardEvent<HTMLDivElement>,
) {
  if ('clipboardData' in e) {
    const pastedText = e.clipboardData.getData('text');
    if (!/^\d+$/.test(pastedText)) {
      e.preventDefault();
    }
  }
}

/** Marketing API prices are integer cents (Angular `centsToDollars`). */
export function centsToUsdAmount(cents: number | null | undefined): number {
  if (cents == null || Number.isNaN(Number(cents))) return 0;
  return Number(cents) / 100;
}

export function formatUsdFromCents(cents: number | null | undefined): string {
  return `$${centsToUsdAmount(cents).toFixed(2)}`;
}

export function currencyFormat(
  price: number | undefined | null,
  currency = 'USD',
  locale = 'en-US',
) {
  const value = centsToUsdAmount(price);

  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(
    value,
  );
}

/** Format a dollar amount that is already in USD (not cents). Rounds to nearest dollar, US commas. */
export function formatAmountUSD(
  amount: string | number | null | undefined,
): string {
  if (amount == null) return '-';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!Number.isFinite(num)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.round(num));
}

// copy to clipboard
export async function doCopyToClipboard(data: string) {
  try {
    await navigator.clipboard.writeText(data);
    return true;
  } catch (error) {
    return Promise.reject(error);
  }
}

export function logErrors(error: unknown) {
  // eslint-disable-next-line no-console
  console.error('Error logs::', error);
}

export function logInfo(...data: unknown[]) {
  // eslint-disable-next-line no-console
  console.log('Info logs::', ...data);
}

/** Converts a raw payment status enum (e.g. "PAYS_AS_AGREED") to sentence case ("Pays as agreed"), matching Angular's accountStatus transformation. */
export function formatPaymentStatus(val?: string): string | undefined {
  if (!val) return val;
  const s = val.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* Function to check the value is boolean or not */
export function isBoolean(val: boolean | undefined | null) {
  if (typeof val === 'boolean') {
    return true;
  }

  return false;
}

/* Function to check the provided value is valid domain URL or not */
export function isValidDomainUrl(url: string | null | undefined) {
  if (!url) {
    return false;
  }

  return REG_EX.websiteDomain.test(url);
}

/* Function to check the provided value is valid email address or not */
export function isValidEmail(val: string | null | undefined) {
  if (!val) {
    return false;
  }

  return REG_EX.email.test(val);
}

/* Function to check the provided value is valid object or not */
export function isValidObj(value: unknown) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/* Function to check the provided value is valid nom empty object or not */
export function isValidNonEmptyObj(value: unknown) {
  return isValidObj(value) && Object.keys(value as object).length > 0;
}

/* Function to check whether a plan's features include at least one credit feature */
export function hasCreditFeature(features: string[]): boolean {
  return (
    features?.some((feature) =>
      CREDIT_FEATURES.includes(feature.toUpperCase()),
    ) ?? false
  );
}

/* Function to remove submitted PII field names from the missingPii list, case-insensitively */
export function removeSubmittedPiiFromMissingList(
  missingPii: string[] | undefined,
  submittedFields: string[],
): string[] {
  const submittedLower = submittedFields.map((field) =>
    field.trim().toLowerCase(),
  );
  return (missingPii ?? []).filter(
    (field) =>
      typeof field !== 'string' ||
      !submittedLower.includes(field.trim().toLowerCase()),
  );
}

type EntitlementCheckData = {
  userFeatures: string[];
  entitlements: string[];
  isAgent: boolean;
  agentCheckRequired?: boolean;
};

/**
 * Determine whether a user has access to at least one required entitlement set.
 *
 * Each string in the `entitlements` array can represent either a single entitlement
 * or a comma-separated set of entitlements. If a string contains commas, all
 * listed entitlements in that string must be present in `userFeatures` for that
 * entitlement set to be considered available.
 *
 * @param {EntitlementCheckData} data - Object containing entitlement check parameters.
 * @param {string[]} data.userFeatures - User's available entitlements/features.
 * @param {string[]} data.entitlements - Array of required entitlement strings.
 *   Each entry may be a single entitlement or a comma-separated list of entitlements.
 * @param {boolean} data.isAgent - True if the current user is an agent.
 * @param {boolean} [data.agentCheckRequired=true] - Whether agent-specific entitlement restrictions should be applied.
 * @returns {boolean} - True when at least one entitlement requirement is satisfied, otherwise false.
 */
export function hasEntitlementAccess(data: EntitlementCheckData): boolean {
  const {
    userFeatures,
    entitlements,
    isAgent,
    agentCheckRequired = true,
  } = data;
  return (
    entitlements?.some((entitlement) => {
      if (!entitlement) {
        return false;
      }

      if (entitlement.includes(',')) {
        return entitlement
          .split(',')
          .map((ent) => ent.trim())
          .every((ent) => userFeatures.includes(ent));
      }

      if (
        isAgent &&
        agentCheckRequired &&
        SERVICE_ENTITLEMENTS_MAPPING[SERVICES.digitalVault].includes(
          entitlement,
        )
      ) {
        return false;
      }

      return userFeatures.includes(entitlement);
    }) ?? false
  );
}

type ServiceAccessData = {
  userFeatures: string[];
  isAgent: boolean;
  rawEntitlements: unknown;
};

/** Pure form of `useIsFeatureAvailable`, for evaluating many services in one pass. */
export function isServiceAvailable(
  service: ServicesType | string[],
  { userFeatures, isAgent, rawEntitlements }: ServiceAccessData,
  agentCheckRequired = true,
): boolean {
  const entitlements = Array.isArray(service)
    ? service
    : SERVICE_ENTITLEMENTS_MAPPING[service as ServicesType];

  if (
    hasEntitlementAccess({
      userFeatures,
      entitlements,
      isAgent,
      agentCheckRequired,
    })
  ) {
    return true;
  }

  if (service === SERVICES.insightsAndTips) {
    return rawEntitlementsIncludeNewsArticles(rawEntitlements);
  }

  return false;
}

export function resolveBureauId(rawName: string): IBureau | undefined {
  const lower = rawName.toLowerCase().replace(/\s+/g, '');

  return (Object.keys(CREDIT_BUREAU_CONFIGS) as IBureau[]).find((id) =>
    (CREDIT_BUREAU_CONFIGS[id].rawAliases as readonly string[]).includes(lower),
  );
}

export function isBureauId(id: string): id is IBureau {
  return id in CREDIT_BUREAU_CONFIGS;
}

export function formatBureauName(name: string): string {
  return (
    CREDIT_BUREAU_CONFIGS[name.toLocaleLowerCase() as IBureau]?.displayName ??
    name
  );
}

/**
 * Stops a click on a same-form nav control from stealing focus away from
 * an adjacent auto-focused input on mousedown, which would otherwise blur
 * that input, trigger validation, shift layout, and cause the click to
 * miss its target before mouseup fires.
 */
export function preventFocusSteal(e: React.MouseEvent) {
  e.preventDefault();
}

/**
 * Resolves after `ms`.
 *
 * Multi-step enrollment uses it to reproduce identityWeb's deliberate two-second hold between a
 * step's submit and its persist call (`ENROLLMENT_STEP_DELAY_MS`), which the flow's pacing — and
 * the "Updating Account" beat members are used to — depends on.
 */
export function delay(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * True when an API error is identity verification denying the member (`DITVerify` + 430).
 * Accepts the response body, or the `{ status, data }` envelope (object or JSON string) the
 * api interceptor rejects with.
 */
export function isDitDenialError(error: unknown): boolean {
  let parsed = error;
  if (typeof error === 'string') {
    try {
      parsed = JSON.parse(error);
    } catch {
      return false;
    }
  }
  if (!parsed || typeof parsed !== 'object') return false;

  const payload = 'data' in parsed ? (parsed as LooseObjectType).data : parsed;
  const body = Array.isArray(payload) ? payload[0] : payload;
  return (
    typeof body?.property === 'string' &&
    body.property.includes(FLEX_DIT_VERIFY_PROPERTY) &&
    Number(body.errorCode) === FLEX_DIT_VERIFY_DENY_CODE
  );
}
